import { useMemo } from 'react';
import { CoinPickerProps } from './props';
import { Label, LabelSkeleton, FormGroup } from './styles';
import { Coin, CoinType } from '@starsoft/common/models';
import AutocompleteSelectorOptionWithImageSkeleton from '../AutocompleteMenuSelector/CustomOptions/AutocompleteSelectorAccount/Skeleton';
import { AutocompleteMenuSelector } from '../AutocompleteMenuSelector';
import { AutocompleteSelectorOptionCoinWithImage } from '@starsoft/common/components';
import { SelectInputWithImage } from '../SelectInput/SelectWithImage/SelectInputWithImage';
import SelectInputWithImageSkeleton from '../SelectInput/SelectWithImage/SelectInputWithImage/Skeleton';
import { SettingsService } from '@starsoft/common/services/SettingsService';

/**
 * CoinPicker component that allows users to select a cryptocurrency coin from a list
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether the component is in loading state
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {(coin: Coin) => void} props.setCoin - Callback function when a coin is selected
 * @param {string} props.label - Label text to display above the picker
 * @param {string} props.modalTitle - Title for the selection modal
 * @param {Coin[]} props.coins - Array of available coins to pick from
 * @param {string|null} props.currentCoinId - ID of currently selected coin
 * @returns {JSX.Element} CoinPicker component with autocomplete selector
 */
export function CoinPicker({
  isLoading,
  disabled,
  setCoin,
  label,
  modalTitle,
  coins,
  currentCoinId,
}: CoinPickerProps) {
  // Determine if component should show loading state
  const loading = isLoading || currentCoinId === null;

  // Filter out demo coins from options
  const options = useMemo(
    () => coins?.filter(item => item?.type != CoinType.Demo),
    [coins],
  );

  /**
   * Handle coin selection
   * @param {Coin} coin - The selected coin
   */
  function handleSelect(coin: Coin) {
    setCoin?.(coin);
  }

  return (
    <FormGroup>
      {loading ? <LabelSkeleton /> : <Label>{label}</Label>}
      <AutocompleteMenuSelector
        disabled={disabled}
        title={modalTitle ?? ''}
        options={options as Coin[]}
        value={currentCoinId}
        filterBy={['symbol', 'name']}
        setValue={handleSelect}
        getOptionLabel={option => option.code}
        getOptionValue={option => option.id}
        loading={loading}
        customComponents={{
          customOption: ({ handleSelect, option, selected }) => (
            <AutocompleteSelectorOptionCoinWithImage
              handleSelect={handleSelect}
              option={option}
              selected={selected}
            />
          ),
          customInput: ({ handleOpen, open, option }) => (
            <SelectInputWithImage
              handleOpen={handleOpen}
              open={open}
              image={`${SettingsService.env.S3_BUCKET_BASE_URL}/coins/rounded/${option?.image}`}
              label={option?.code ?? ''}
              disabled={disabled}
            />
          ),
          customSkeletonOption: () => (
            <AutocompleteSelectorOptionWithImageSkeleton />
          ),
          customSkeletonInput: () => <SelectInputWithImageSkeleton />,
        }}
      />
    </FormGroup>
  );
}
